import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { statsApi, securityApi } from '../../lib/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Home, 
  FileText,
  FileSpreadsheet,
  FileDown, 
  Users, 
  CreditCard, 
  AlertTriangle, 
  TrendingUp,
  ArrowRight,
  Clock,
  Euro,
  ArrowDownCircle,
  ArrowUpCircle,
  Percent,
  CalendarClock,
  Bell,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  BarChart3,
  CheckCircle,
  ShieldAlert,
  Lock,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const fmtEuro = (n) => '€ ' + (Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DeltaBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? 'text-green-600' : 'text-red-600'}`} title="Variazione sul mese precedente">
      <Icon className="w-3 h-3" />{Math.abs(pct)}%
    </span>
  );
};

const RecapMensile = ({ recap, highlight }) => {
  if (!recap) return null;
  return (
    <Card className={`border ${highlight ? 'border-amber-200 bg-gradient-to-r from-amber-50/40 to-white' : 'border-slate-200 bg-white'}`} data-testid={`recap-mensile-${recap.mese}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Euro className="w-4 h-4 text-amber-600" />
          Recap {recap.mese_label}
          <span className="text-xs font-normal text-slate-500 ml-1">({recap.contratti_attivi} contratti)</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <ArrowDownCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
            <p className="text-xs text-slate-600 font-medium">Incassi SDD</p>
            <p className="text-lg md:text-xl font-bold text-green-700">{fmtEuro(recap.incassi_sdd)}</p>
            <div className="mt-0.5 h-4"><DeltaBadge pct={recap.delta_incassi_pct} /></div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <ArrowUpCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
            <p className="text-xs text-slate-600 font-medium">Bonifici OUT</p>
            <p className="text-lg md:text-xl font-bold text-red-700">{fmtEuro(recap.bonifici_out)}</p>
            <div className="mt-0.5 h-4" />
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <Percent className="w-6 h-6 text-amber-600 mx-auto mb-1" />
            <p className="text-xs text-slate-600 font-medium">Margine SA</p>
            <p className="text-lg md:text-xl font-bold text-amber-700">{fmtEuro(recap.margine_semplificaffitto)}</p>
            <div className="mt-0.5 h-4"><DeltaBadge pct={recap.delta_margine_pct} /></div>
            <div className="mt-1 text-[11px] text-slate-500 space-y-0.5">
              <p>Imp.: {fmtEuro(recap.margine_imponibile)}</p>
              <p>IVA 22%: {fmtEuro(recap.margine_iva)}</p>
            </div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <Home className="w-6 h-6 text-blue-600 mx-auto mb-1" />
            <p className="text-xs text-slate-600 font-medium">Canoni Gestiti</p>
            <p className="text-lg md:text-xl font-bold text-blue-700">{fmtEuro(recap.canoni_gestiti)}</p>
            <div className="mt-0.5 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [secAlerts, setSecAlerts] = useState(null);
  const [secDismissed, setSecDismissed] = useState(false);
  const [secExpanded, setSecExpanded] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchSecurityAlerts();
  }, []);

  const fetchSecurityAlerts = async () => {
    try {
      const res = await securityApi.getAlerts();
      setSecAlerts(res.data);
    } catch (e) {
      // Silenzioso: il banner è un extra, non deve rompere la dashboard
      console.error('Security alerts error:', e);
    }
  };

  const handleMarkSeen = async () => {
    setSecDismissed(true);
    try {
      await securityApi.markSeen();
      toast.success('Avvisi di sicurezza segnati come visti');
    } catch (e) {
      toast.error('Errore nel salvataggio; gli avvisi ricompariranno al prossimo accesso');
    }
  };

  const handleExport = async (formato) => {
    try {
      setExporting(true);
      const res = await statsApi.exportRecap(formato);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `recap_semplificaffitto.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Recap ${formato === 'pdf' ? 'PDF' : 'Excel'} scaricato`);
    } catch (e) {
      toast.error("Errore durante l'esportazione del recap");
    } finally {
      setExporting(false);
    }
  };


  const fetchStats = async () => {
    try {
      const response = await statsApi.getDashboard();
      setStats(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle statistiche');
      console.error('Stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Immobili',
      value: stats?.properties || 0,
      icon: Home,
      color: 'bg-blue-100 text-blue-600',
      path: '/admin/immobili'
    },
    {
      title: 'Contratti Attivi',
      value: stats?.contracts || 0,
      icon: FileText,
      color: 'bg-emerald-100 text-emerald-600',
      path: '/admin/contratti'
    },
    {
      title: 'Scadenziario',
      value: stats?.avvisi_attivi || 0,
      icon: CalendarClock,
      color: (stats?.avvisi_attivi || 0) > 0 ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500',
      path: '/admin/avvisi'
    },
    {
      title: 'Solleciti',
      value: stats?.solleciti_aperti || 0,
      icon: Bell,
      color: (stats?.solleciti_aperti || 0) > 0 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500',
      path: '/admin/solleciti'
    }
  ];

  const alertCards = [
    {
      title: 'Pagamenti In Attesa',
      value: stats?.payments_pending || 0,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      path: '/admin/pagamenti'
    },
    {
      title: 'Pagamenti Aperti',
      value: stats?.payments_open || 0,
      icon: CreditCard,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      path: '/admin/pagamenti'
    },
    {
      title: 'Pagamenti Completati',
      value: stats?.payments_completed || 0,
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      path: '/admin/pagamenti'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-500 mt-1">Panoramica del tuo gestionale affitti</p>
          </div>
          <Button 
            onClick={() => navigate('/admin/contratti')}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="new-contract-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Nuovo Contratto
          </Button>
        </div>
      </div>

      {/* Page Content */}
      <div className="page-content animate-fade-in">
        {/* Banner Avvisi di Sicurezza */}
        {!secDismissed && secAlerts && secAlerts.count > 0 && (
          <div
            className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 shadow-sm"
            data-testid="security-alert-banner"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0 rounded-lg bg-amber-100 p-2">
                <ShieldAlert className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-amber-900">
                    Avvisi di sicurezza · {secAlerts.count} event{secAlerts.count === 1 ? 'o' : 'i'} recent{secAlerts.count === 1 ? 'e' : 'i'}
                  </h3>
                  <button
                    onClick={handleMarkSeen}
                    className="flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 transition-colors flex-shrink-0"
                    data-testid="security-alert-seen-btn"
                    title="Nascondi dalla dashboard (restano nel Centro Sicurezza)"
                  >
                    <CheckCircle className="w-3.5 h-3.5" /> Visto
                  </button>
                </div>

                {/* Riepilogo per tipo */}
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-amber-800">
                  {secAlerts.summary?.login_failed > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> {secAlerts.summary.login_failed} login falliti
                    </span>
                  )}
                  {(secAlerts.summary?.account_auto_locked > 0 || secAlerts.locked_accounts_now > 0) && (
                    <span className="inline-flex items-center gap-1">
                      <Lock className="w-3.5 h-3.5" /> {secAlerts.locked_accounts_now} account bloccati ora
                    </span>
                  )}
                  {secAlerts.summary?.unauthorized_access > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <ShieldAlert className="w-3.5 h-3.5" /> {secAlerts.summary.unauthorized_access} accessi negati
                    </span>
                  )}
                </div>

                {/* Dettaglio eventi (espandibile) */}
                {secExpanded && (
                  <ul className="mt-3 space-y-1.5 border-t border-amber-200 pt-2" data-testid="security-alert-list">
                    {secAlerts.events.slice(0, 15).map((ev, i) => (
                      <li key={i} className="flex flex-wrap items-center gap-x-2 text-xs text-amber-900">
                        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${ev.is_lock ? 'bg-red-500' : 'bg-amber-500'}`} />
                        <span className="font-medium">{ev.label}</span>
                        {ev.email && <span className="text-amber-700">· {ev.email}</span>}
                        {ev.ip && <span className="text-amber-600">· IP {ev.ip}</span>}
                        <span className="text-amber-500">· {ev.timestamp ? new Date(ev.timestamp).toLocaleString('it-IT') : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-2 flex items-center gap-4 text-xs">
                  <button
                    onClick={() => setSecExpanded(v => !v)}
                    className="font-medium text-amber-700 hover:text-amber-900 underline underline-offset-2"
                    data-testid="security-alert-toggle"
                  >
                    {secExpanded ? 'Nascondi dettagli' : 'Mostra dettagli'}
                  </button>
                  <button
                    onClick={() => navigate('/admin/centro-sicurezza')}
                    className="inline-flex items-center gap-1 font-medium text-amber-700 hover:text-amber-900"
                  >
                    Vai al Centro Sicurezza <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => (
            <Card 
              key={stat.title} 
              className="stat-card cursor-pointer card-hover"
              onClick={() => navigate(stat.path)}
              data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`stat-card-icon ${stat.color}`}>
                    <stat.icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Payments Overview - solo visualizzazione, nessun link */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {alertCards.map((alert) => (
            <Card 
              key={alert.title}
              className={`${alert.bgColor} border-0`}
              data-testid={`alert-${alert.title.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <alert.icon className={`w-8 h-8 ${alert.color}`} strokeWidth={1.5} />
                  <div>
                    <p className="text-sm font-medium text-slate-600">{alert.title}</p>
                    <p className={`text-3xl font-bold ${alert.color}`}>{alert.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Due file di recap mensili (corrente + precedente) con variazione Δ% */}
        <div className="grid grid-cols-1 gap-4 mb-6">
          <RecapMensile recap={stats?.recap_mese} highlight />
          <RecapMensile recap={stats?.recap_mese_precedente} />
        </div>

        {/* Statistica annuale */}
        {stats?.recap_anno && (
          <Card className="mb-6 border border-indigo-200 bg-gradient-to-r from-indigo-50/50 to-white" data-testid="recap-annuale">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-indigo-600" />
                  Statistica Anno {stats.recap_anno.anno}
                  <span className="text-xs font-normal text-slate-500 ml-1">(gen–dic)</span>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExport('excel')} disabled={exporting} data-testid="export-excel-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-1 text-green-600" /> Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting} data-testid="export-pdf-btn">
                    <FileDown className="w-4 h-4 mr-1 text-red-600" /> PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-slate-600 font-medium">Incassi SDD</p>
                  <p className="text-base md:text-lg font-bold text-green-700">{fmtEuro(stats.recap_anno.incassi_sdd)}</p>
                  <div className="mt-0.5 h-4"><DeltaBadge pct={stats.recap_anno.delta_incassi_pct} /></div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-xs text-slate-600 font-medium">Bonifici OUT</p>
                  <p className="text-base md:text-lg font-bold text-red-700">{fmtEuro(stats.recap_anno.bonifici_out)}</p>
                  <div className="mt-0.5 h-4" />
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-slate-600 font-medium">Margine SA</p>
                  <p className="text-base md:text-lg font-bold text-amber-700">{fmtEuro(stats.recap_anno.margine_semplificaffitto)}</p>
                  <div className="mt-0.5 h-4"><DeltaBadge pct={stats.recap_anno.delta_margine_pct} /></div>
                  {stats.recap_anno.anno_precedente?.ha_dati && (
                    <p className="text-[10px] text-slate-500 mt-0.5">{stats.recap_anno.anno_precedente.anno}: {fmtEuro(stats.recap_anno.anno_precedente.margine_semplificaffitto)}</p>
                  )}
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-600 font-medium">Margine medio/mese</p>
                  <p className="text-base md:text-lg font-bold text-slate-700">{fmtEuro(stats.recap_anno.margine_medio_mensile)}</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-xs text-slate-600 font-medium">Fatture emesse</p>
                  <p className="text-base md:text-lg font-bold text-purple-700">{stats.recap_anno.n_fatture}</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg" title="Percentuale di canoni incassati sui mesi già conclusi (il mese in corso è escluso)">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-xs text-slate-600 font-medium">Tasso incasso</p>
                  <p className="text-base md:text-lg font-bold text-emerald-700">{stats.recap_anno.tasso_incasso}%</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{stats.recap_anno.pagamenti_completati}/{stats.recap_anno.pagamenti_totali} · mesi conclusi</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-slate-600 font-medium">IVA su margine</p>
                  <p className="text-base md:text-lg font-bold text-blue-700">{fmtEuro(stats.recap_anno.margine_iva)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Box operativi: scaduti + contratti in scadenza */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className={`border ${(stats?.pagamenti_scaduti?.count || 0) > 0 ? 'border-red-200 bg-red-50/40' : 'border-slate-200'}`} data-testid="box-pagamenti-scaduti">
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${(stats?.pagamenti_scaduti?.count || 0) > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                <AlertCircle className={`w-6 h-6 ${(stats?.pagamenti_scaduti?.count || 0) > 0 ? 'text-red-600' : 'text-slate-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-600 font-medium">Pagamenti scaduti</p>
                <p className="text-2xl font-bold text-red-700">{stats?.pagamenti_scaduti?.count || 0}</p>
                <p className="text-xs text-slate-500">Importo: {fmtEuro(stats?.pagamenti_scaduti?.importo)}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/admin/pagamenti')} data-testid="goto-pagamenti-btn">
                Vai <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>

          <Card className={`border ${(stats?.contratti_in_scadenza?.count || 0) > 0 ? 'border-orange-200 bg-orange-50/40' : 'border-slate-200'}`} data-testid="box-contratti-scadenza">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${(stats?.contratti_in_scadenza?.count || 0) > 0 ? 'bg-orange-100' : 'bg-slate-100'}`}>
                  <CalendarClock className={`w-6 h-6 ${(stats?.contratti_in_scadenza?.count || 0) > 0 ? 'text-orange-600' : 'text-slate-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-600 font-medium">Contratti in scadenza (60 gg)</p>
                  <p className="text-2xl font-bold text-orange-700">{stats?.contratti_in_scadenza?.count || 0}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/admin/contratti')} data-testid="goto-contratti-btn">
                  Vai <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              {(stats?.contratti_in_scadenza?.lista || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-orange-100 space-y-1">
                  {stats.contratti_in_scadenza.lista.map((c) => (
                    <div key={c.codice} className="flex items-center justify-between text-xs">
                      <span className="font-mono text-slate-600">{c.codice}</span>
                      <span className="text-slate-500">scade il {c.data_fine?.split('-').reverse().join('/')}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Grafico lineare andamento margine: anno solare vs anno precedente */}
        {(stats?.andamento_margine || []).length > 0 && (
          <Card className="mb-8 border border-slate-200" data-testid="chart-andamento-margine">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-600" />
                Andamento Margine SA — {stats?.andamento_meta?.anno_corrente}
                {stats?.andamento_meta?.ha_dati_precedente && ` vs ${stats?.andamento_meta?.anno_precedente}`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.andamento_margine} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="mese_label" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => `€${(v / 1000).toFixed(1)}k`} />
                  <Tooltip
                    formatter={(v, name) => [v == null ? '—' : fmtEuro(v), name]}
                    labelStyle={{ color: '#0f172a', fontWeight: 600 }}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="corrente" name={`${stats?.andamento_meta?.anno_corrente}`} stroke="#d97706" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} />
                  {stats?.andamento_meta?.ha_dati_precedente && (
                    <Line type="monotone" dataKey="precedente" name={`${stats?.andamento_meta?.anno_precedente}`} stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2.5 }} connectNulls={false} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Security 2FA Adoption rimossa — disponibile in Centro Sicurezza */}

        {/* Azioni Rapide rimosse — la navigazione avviene dalla sidebar */}
      </div>
    </div>
  );
};

export default DashboardPage;
